import { useActions, useValues } from 'kea';
import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { RouteComponentProps, useParams, withRouter } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { IntlContext } from '../../IntlProviderWrapper';
import {
    ComparativeByIndividualReportActions,
    comparativeByIndividualReportLogic, ComparativeByIndividualReportProps
} from '../../redux/comparativeByIndividualReportLogic';
import { ComparativeReportByIndividual } from './ComparativeReportByIndividual';
import { BeingBevi } from '../../components/BeingBevi';
import { t } from '../../intlSingleton';
import { Tab } from 'semantic-ui-react';
import { useBeingBeviStore } from '../../redux/beingBevi.store';
import { HasPermissionCapability, IsParticipant, IsSuperAdmin } from '../../utils/userHelper';
import { UserPermissions } from '../../services/usersService';
import { appLogic, AppProps } from '../../redux/appLogic';
import { ComparativeIndividualReportModel, ReportLanguage } from '../../services/reportService';
import { AcceptBeingBevi } from '../../components/AcceptBeingBevi';
import { Spin } from 'antd';
import { ReportMarkdown } from '../ComparativeIndividualReport/ReportView';

type Props = RouteComponentProps;

export const ComparativeReportByIndividualContainer = withRouter((props: Props): JSX.Element => {
    const { loadComparativeByIndividualReport, downloadWithinGroupReport }: ComparativeByIndividualReportActions = useActions(comparativeByIndividualReportLogic);
    const { loading, report, loadingDataDownload }: ComparativeByIndividualReportProps = useValues(comparativeByIndividualReportLogic);
    const { getReportLanguage } = useContext(IntlContext);
    const { hasAccept, loadingMarkdown, loadingAccept, markdown, markdownError, hasAcceptBeingBevi, acceptBeingBevi, getMarkdown, clear } = useBeingBeviStore();
    const { tokenInfo }: AppProps = useValues(appLogic);
    const [tab, setTab] = useState<number>(0);
    const { id } = useParams<{ id: string }>();

    const handleAcceptBeingBevi = useCallback(() => {
        acceptBeingBevi({ reportId: Number(id) });
    }, [acceptBeingBevi, id]);

    useEffect(() => {
        return () => {
            clear();
        }
    }, [clear])

    useEffect(() => {
        hasAcceptBeingBevi({ reportId: Number(id) });
    }, [id, hasAcceptBeingBevi]);


    useEffect(() => {
        loadComparativeByIndividualReport(id)
    }, [loadComparativeByIndividualReport, id]);

    const download = () => {
        const reportLanguage = getReportLanguage && getReportLanguage();
        downloadWithinGroupReport(reportLanguage ?? ReportLanguage.English, id)
    }

    if (loading) return <Loading />

    return (
        <Fragment>
            <Tab activeIndex={tab} onTabChange={(e, data) => setTab(data.activeIndex as number)} menu={{ pointing: true }} panes={[{
                menuItem: t('app.reports.comparative_by_individual.title'),
                render: () => (<Tab.Pane>
                    <ComparativeReportByIndividual
                        report={report ? report : {}}
                        download={download}
                        loadingDataDownload={loadingDataDownload}
                        scalesTitle={{
                            negative_life_events: t('app.report.scales.negative_life_events'),
                            needs_closure: t('app.report.scales.needs_closure'),
                            needs_fulfillment: t('app.report.scales.needs_fulfillment'),
                            identity_diffusion: t('app.report.scales.identity_diffusion'),
                            basic_openness: t('app.report.scales.basic_openness'),
                            self_certitude: t('app.report.scales.self_certitude'),
                            basic_determinism: t('app.report.scales.basic_determinism'),
                            socioemotional_convergence: t('app.report.scales.socioemotional_convergence'),
                            physical_resonance: t('app.report.scales.physical_resonance'),
                            emotional_attunement: t('app.report.scales.emotional_attunement'),
                            self_awareness: t('app.report.scales.self_awareness'),
                            meaning_quest: t('app.report.scales.meaning_quest'),
                            religious_traditionalism: t('app.report.scales.religious_traditionalism'),
                            gender_traditionalism: t('app.report.scales.gender_traditionalism'),
                            sociocultural_openness: t('app.report.scales.sociocultural_openness'),
                            ecological_resonance: t('app.report.scales.ecological_resonance'),
                            global_resonance: t('app.report.scales.global_resonance'),
                            consistency: t('app.report.scales.consistency'),
                            congruency: t('app.report.scales.congruency'),
                        }}
                    /></Tab.Pane>)
            },
            ...(IsSuperAdmin(tokenInfo) || HasPermissionCapability(tokenInfo, UserPermissions.visualizeBBInsights) ? [{
                menuItem: {
                    key: 'tab1', content: t('app.report.insigths'), title: t('app.report.insightsDescription')
                },
                render: () => {
                    if (markdown == null && !loadingMarkdown && hasAccept && !markdownError) {
                        const reportLanguage = getReportLanguage && getReportLanguage();
                        const obj: ComparativeIndividualReportModel = {
                            generateArea: IsParticipant(tokenInfo) ? 'Participant' : 'Admin',
                            language: reportLanguage ?? ReportLanguage.English,
                            formIds: [],
                            reportId: Number(id)
                        };
                        getMarkdown(obj);
                    }

                    if (markdownError)
                        return <p>Error</p>;

                    if (!hasAccept) {
                        return <Tab.Pane>
                            <AcceptBeingBevi acceptBeingBevi={handleAcceptBeingBevi} loading={loadingAccept} onCancel={() => setTab(0)} open={tab === 1} />
                        </Tab.Pane>
                    }

                    if (loadingMarkdown) {
                        return <Tab.Pane>
                            <Spin spinning />
                        </Tab.Pane>
                    }

                    return <Tab.Pane>
                        <ReportMarkdown markdown={markdown} />
                    </Tab.Pane>
                }
            }] : [])
            ]} />
            <BeingBevi type='ComparativeByIndividualReport' id={id} />
        </Fragment>
    )
});
